import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, Coffee, ArrowRight, Store, LogIn, Smartphone, CreditCard, Gift, Bell, LayoutDashboard, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { sized } from "@/components/FoodImg";
import { api } from "@/lib/api";
import { useAuth, adminAuthStore } from "@/lib/auth";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/5373256/pexels-photo-5373256.jpeg";

// QRDine platform homepage — all about the product.
//   • Owner/manager/super already signed in → straight to their console.
//   • Signed-in customer → "Your restaurants" (most recent first) to reorder.
//   • Everyone else → the QRDine pitch + how it works + enquiry form.
export default function Landing() {
  const auth = useAuth();
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState(null);

  useEffect(() => {
    const admin = adminAuthStore.get();
    if (admin?.token) nav(admin.user?.role === "super_admin" ? "/super" : "/admin/dashboard", { replace: true });
  }, [nav]);

  useEffect(() => {
    if (auth) api.get("/orders/customer/restaurants").then((r) => setRestaurants(r.data)).catch(() => setRestaurants([]));
    else setRestaurants(null);
  }, [auth]);

  const firstName = (auth?.user?.name || "").split(" ")[0];
  const mine = (restaurants || []).filter((r) => r.is_active !== false);
  const scrollToEnquire = () => document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain">
      <nav className="max-w-7xl mx-auto px-5 sm:px-10 pt-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[var(--qd-primary)] grid place-items-center"><Coffee className="w-5 h-5 text-white" aria-hidden /></div>
          <span className="font-display font-bold text-xl tracking-tight">QRDine</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {auth
            ? <Link to="/profile" data-testid="landing-profile" className="text-sm font-semibold text-[var(--qd-text)] px-3 py-2">Hi, {firstName || "there"}</Link>
            : <Link to="/login" data-testid="landing-signin" className="text-sm text-[var(--qd-muted)] hover:text-[var(--qd-text)] px-2 sm:px-3 py-2 inline-flex items-center gap-1 whitespace-nowrap"><LogIn className="w-4 h-4" aria-hidden /> Sign in</Link>}
          {/* whitespace-nowrap: both labels wrapped to two lines at 375px */}
          <Link to="/admin/login" data-testid="landing-admin-link" className="text-sm text-[var(--qd-muted)] hover:text-[var(--qd-text)] px-2 sm:px-3 py-2 whitespace-nowrap">Restaurant Owner →</Link>
        </div>
      </nav>

      {/* Signed-in customer with history → their restaurants first */}
      {auth && mine.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-10 pt-10 sm:pt-12">
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Welcome back{firstName ? `, ${firstName}` : ""}</h1>
          <p className="text-[var(--qd-muted)] mt-1 mb-6">Jump back into a place you've ordered at.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mine.map((r) => <RestaurantCard key={r.id} r={r} nav={nav} />)}
          </div>
          <div className="mt-6 text-sm text-[var(--qd-muted)]">Somewhere new? Scan the QR on your table to start.</div>
        </section>
      )}

      {/* Pitch (guests, or a signed-in customer with no history yet) */}
      {(!auth || (restaurants !== null && mine.length === 0)) && (
        <>
          <section className="max-w-7xl mx-auto px-5 sm:px-10 pt-12 sm:pt-16 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6 sm:space-y-8">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--qd-cream)] text-[var(--qd-text)] text-xs font-mono-alt uppercase tracking-widest">
                <QrCode className="w-3.5 h-3.5" aria-hidden /> Scan · Order · Pay
              </span>
              <h1 className="font-display text-[2.75rem] leading-[1] sm:text-6xl lg:text-7xl font-black sm:leading-[0.95] tracking-tighter">
                Scan the table.<br /><span className="text-[var(--qd-primary)]">Order.</span> Skip the wait.
              </h1>
              <p className="text-base sm:text-lg text-[var(--qd-muted)] max-w-xl leading-relaxed">
                QRDine turns any restaurant table into a checkout counter — guests order in seconds, pay from their phone, and earn loyalty. One platform for every café you run.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
                <Button data-testid="landing-enquire-btn" onClick={scrollToEnquire}
                  className="h-14 px-8 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)] text-white text-base font-semibold qd-shadow-lift">
                  Get in touch <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
                </Button>
                <Link to="/admin/login" className="contents">
                  <Button variant="outline" data-testid="landing-admin-btn" className="h-14 px-8 rounded-full border-[var(--qd-text)] text-[var(--qd-text)] hover:bg-[var(--qd-text)] hover:text-white transition-colors">
                    <Store className="w-4 h-4 mr-2" aria-hidden /> For restaurants
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] overflow-hidden qd-shadow-lift aspect-[4/5]">
                <img src={sized(HERO, 1200)} alt="Restaurant table" loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 qd-hero-scrim" />
              </div>
              <div className="absolute -top-5 -right-3 sm:-top-6 sm:-right-6 w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-white qd-shadow-soft grid place-items-center rotate-6">
                <QrCode className="w-10 h-10 sm:w-12 sm:h-12 text-[var(--qd-text)]" aria-hidden />
              </div>
            </motion.div>
          </section>

          {/* How it works */}
          <section className="max-w-7xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-center mb-2">How it works</h2>
            <p className="text-[var(--qd-muted)] text-center mb-10">Three taps from sitting down to served.</p>
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                { n: "1", icon: QrCode, t: "Scan the QR", d: "Guests scan the code on the table — no app to install, no sign-up wall." },
                { n: "2", icon: Utensils, t: "Order from the phone", d: "Browse the menu, customise, and send the order straight to the kitchen." },
                { n: "3", icon: CreditCard, t: "Pay & earn loyalty", d: "Pay by UPI or at the counter, track the order live, and rack up points." },
              ].map((s) => (
                <div key={s.n} className="bg-white rounded-3xl p-6 border border-[var(--qd-border)]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--qd-cream)] grid place-items-center"><s.icon className="w-5 h-5 text-[var(--qd-primary)]" aria-hidden /></div>
                    <span className="font-mono-alt text-xs text-[var(--qd-muted)]">STEP {s.n}</span>
                  </div>
                  <div className="font-display font-bold text-lg">{s.t}</div>
                  <p className="text-sm text-[var(--qd-muted)] mt-1 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Features */}
          <section className="max-w-7xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-center mb-10">Everything a modern café needs</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: Smartphone, t: "No app, no friction", d: "Works in any phone browser. Scan and you're on the menu." },
                { icon: CreditCard, t: "Pay from the phone", d: "UPI QR or intent, or pay at the counter — the choice is the guest's." },
                { icon: Gift, t: "Built-in loyalty", d: "Points and coupons that bring guests back, per restaurant." },
                { icon: Bell, t: "Live order tracking", d: "Guests watch their order go from received to served in real time." },
                { icon: LayoutDashboard, t: "Owner dashboard", d: "Menu, tables, staff, offers, and analytics in one console." },
                { icon: Store, t: "Every café you run", d: "One account, multiple restaurants, each with its own menu and brand." },
              ].map((f) => (
                <div key={f.t} className="bg-white rounded-3xl p-6 border border-[var(--qd-border)]">
                  <div className="w-11 h-11 rounded-xl bg-[var(--qd-cream)] grid place-items-center mb-4"><f.icon className="w-5 h-5 text-[var(--qd-primary)]" aria-hidden /></div>
                  <div className="font-display font-bold text-lg">{f.t}</div>
                  <p className="text-sm text-[var(--qd-muted)] mt-1 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Enquiry */}
          <section id="enquire" className="max-w-3xl mx-auto px-5 sm:px-10 py-14 sm:py-20 scroll-mt-6">
            <div className="bg-white rounded-[2rem] p-6 sm:p-10 border border-[var(--qd-border)] qd-shadow-soft">
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Bring QRDine to your restaurant</h2>
              <p className="text-[var(--qd-muted)] mt-2 mb-8">Tell us a little about your place and we'll get you set up. Questions welcome too.</p>
              <EnquiryForm />
            </div>
          </section>

          <footer className="border-t border-[var(--qd-border)]">
            <div className="max-w-7xl mx-auto px-5 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[var(--qd-muted)]">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-[var(--qd-primary)] grid place-items-center"><Coffee className="w-3.5 h-3.5 text-white" aria-hidden /></div>
                <span className="font-display font-bold text-[var(--qd-text)]">QRDine</span>
              </div>
              <span>Scan · Order · Pay — for every table.</span>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

function RestaurantCard({ r, nav }) {
  const go = () => {
    if (r.last_table_token) nav(`/m/${r.last_table_token}`);
    else toast.message("Scan the table's QR at this restaurant to order.");
  };
  return (
    <button data-testid={`home-restaurant-${r.id}`} onClick={go} className="text-left qd-admin-card p-5 hover:shadow-md transition-shadow flex items-center gap-4">
      {r.logo_url
        ? <img src={r.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover" />
        : <div className="w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br from-[var(--qd-cream)] to-orange-100 font-display font-black text-[var(--qd-primary)]/50 text-xl">{(r.name || "?").charAt(0)}</div>}
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold truncate">{r.name}</div>
        <div className="text-xs text-[var(--qd-primary)] font-semibold inline-flex items-center gap-1">Order again <ArrowRight className="w-3.5 h-3.5" aria-hidden /></div>
      </div>
    </button>
  );
}

function EnquiryForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", restaurant: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      toast.success("Thanks — we'll be in touch soon!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send that — check your connection and try again.");
    }
    setBusy(false);
  };

  if (sent) return (
    <div data-testid="enquiry-sent" className="text-center py-8">
      <div className="w-14 h-14 rounded-2xl bg-[var(--qd-cream)] grid place-items-center mx-auto mb-4"><Coffee className="w-7 h-7 text-[var(--qd-primary)]" aria-hidden /></div>
      <div className="font-display text-xl font-bold">Got it — thank you!</div>
      <p className="text-sm text-[var(--qd-muted)] mt-1">We'll reply at <b>{form.email}</b> shortly.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input data-testid="enquiry-name" required placeholder="Your name" value={form.name} onChange={set("name")} className="h-12 rounded-xl" />
        <Input data-testid="enquiry-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="h-12 rounded-xl" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input data-testid="enquiry-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className="h-12 rounded-xl" />
        <Input data-testid="enquiry-restaurant" placeholder="Restaurant name (optional)" value={form.restaurant} onChange={set("restaurant")} className="h-12 rounded-xl" />
      </div>
      <Textarea data-testid="enquiry-message" required placeholder="How can we help?" value={form.message} onChange={set("message")} className="rounded-xl min-h-[120px]" />
      <Button data-testid="enquiry-submit" type="submit" disabled={busy} className="w-full h-14 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)] text-white text-base font-semibold">
        {busy ? "Sending…" : "Send enquiry"}
      </Button>
    </form>
  );
}
